import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Statistic, Spin, message, Table, Tag, Select, Button, DatePicker } from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  TagsOutlined,
  DollarOutlined,
  ReloadOutlined,
  StopOutlined
} from '@ant-design/icons';
import api from '../../../axiosConfig';
import dayjs from '../../../utils/dayjs';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useUser } from '../../../UserContext';

const { Option } = Select;
const { RangePicker } = DatePicker;

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const ControlServiciosDashboard = () => {
  const { user: currentUser } = useUser();

  // Función para verificar permisos del usuario
  const hasPermission = useCallback(
    (permission) => {
      const module = currentUser?.modulos?.find((m) => m.key === "Control Servicios");
      if (!module || module.estado !== "disponible") return false;

      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "Control Servicios" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  const [loading, setLoading] = useState(false);
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    terminados: 0,
    fallidos: 0,
    cancelados: 0,
    porEstado: {
      recibido: 0,
      facturado: 0,
      sin_estado: 0
    },
    recientes: []
  });
  const [rangoFecha, setRangoFecha] = useState('hoy');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState([
    dayjs().subtract(1, 'month'),
    dayjs(),
  ]);

  const getRangoFechas = useCallback((rango) => {
    const hoy = dayjs();

    switch (rango) {
      case 'personalizado':
        return {
          inicio: customDateRange[0].startOf('day'),
          fin: customDateRange[1].endOf('day'),
        };
      case 'hoy':
        return { inicio: hoy.startOf('day'), fin: hoy.endOf('day') };
      case 'ayer':
        return {
          inicio: hoy.subtract(1, 'day').startOf('day'),
          fin: hoy.subtract(1, 'day').endOf('day'),
        };
      case 'mañana':
        return {
          inicio: hoy.add(1, 'day').startOf('day'),
          fin: hoy.add(1, 'day').endOf('day'),
        };
      case 'semana':
        return { inicio: hoy.startOf('week'), fin: hoy.endOf('week') };
      case 'mes':
        return { inicio: hoy.startOf('month'), fin: hoy.endOf('month') };
      case 'mes_anterior':
        return {
          inicio: hoy.subtract(1, 'month').startOf('month'),
          fin: hoy.subtract(1, 'month').endOf('month'),
        };
      default:
        return { inicio: hoy.startOf('day'), fin: hoy.endOf('day') };
    }
  }, [customDateRange]);

  const fetchEstadisticas = useCallback(async (rango) => {
    let inicio, fin;

    if (rango === 'personalizado') {
      inicio = customDateRange[0].startOf('day');
      fin = customDateRange[1].endOf('day');
    } else {
      const fechas = getRangoFechas(rango);
      inicio = fechas.inicio;
      fin = fechas.fin;
    }

    const fechaInicioStr = inicio.format('YYYY-MM-DD');
    const fechaFinStr = fin.format('YYYY-MM-DD');

    setLoading(true);
    try {
      const response = await api.get('/control-servicios/estadisticas', {
        params: { fechaInicio: fechaInicioStr, fechaFin: fechaFinStr }
      });
      if (response.data.success) {
        setEstadisticas(response.data.data);
      }
    } catch (error) {
      message.error('Error al cargar estadísticas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [customDateRange, getRangoFechas]);

  useEffect(() => {
    if (rangoFecha !== 'personalizado') {
      fetchEstadisticas(rangoFecha);
    }
  }, [rangoFecha, fetchEstadisticas]);

  const handleRangoChange = (value) => {
    setRangoFecha(value);
    setShowCustomPicker(value === 'personalizado');
    if (value !== 'personalizado') {
      setCustomDateRange([]);
      fetchEstadisticas(value);
    }
  };

  const handleCustomRangeChange = (dates) => {
    if (!dates || dates.length !== 2) {
      return;
    }

    const [start, end] = dates;

    // Validación de máximo 2 meses
    const diffMonths = end.diff(start, 'month', true);
    if (diffMonths > 2) {
      message.error('El rango no puede ser mayor a 2 meses');
      return;
    }

    setCustomDateRange(dates);
  };

  const handleRefresh = () => {
    fetchEstadisticas(rangoFecha);
  };

  // Datos para gráfico de estados de auditoría (filtrados por permisos)
  const estadosData = useMemo(() => {
    const labels = [];
    const data = [];
    const backgroundColor = [];
    const borderColor = [];

    // Solo incluir "Recibido" si tiene permiso
    if (hasPermission('RECIBIDO_ETIQUETA_CS')) {
      labels.push('Recibido');
      data.push(estadisticas.porEstado?.recibido || 0);
      backgroundColor.push('rgba(82, 196, 26, 0.6)');
      borderColor.push('rgba(82, 196, 26, 1)');
    }

    // Solo incluir "Facturado" si tiene permiso
    if (hasPermission('FACTURADO_ETIQUETA_CS')) {
      labels.push('Facturado');
      data.push(estadisticas.porEstado?.facturado || 0);
      backgroundColor.push('rgba(24, 144, 255, 0.6)');
      borderColor.push('rgba(24, 144, 255, 1)');
    }

    // Siempre incluir "Sin Estado"
    labels.push('Sin Estado');
    data.push(estadisticas.porEstado?.sin_estado || 0);
    backgroundColor.push('rgba(128, 128, 128, 0.6)');
    borderColor.push('rgba(128, 128, 128, 1)');

    return {
      labels,
      datasets: [
        {
          label: 'Controles por Estado',
          data,
          backgroundColor,
          borderColor,
          borderWidth: 1,
        },
      ],
    };
  }, [estadisticas.porEstado, hasPermission]);

  // Columnas para tabla de controles recientes (memoizadas con permisos)
  const columnasRecientes = useMemo(() => {
    const cols = [
      {
        title: 'Código PCS',
        dataIndex: 'codigo_pcs',
        key: 'codigo_pcs',
        width: 150,
      },
      {
        title: 'Paciente',
        dataIndex: 'nombre_paciente',
        key: 'nombre_paciente',
        width: 200,
      },
      {
        title: 'Estado Auditoría',
        dataIndex: 'estado_auditoria',
        key: 'estado_auditoria',
        width: 130,
        render: (estado) => {
          const estadoColors = {
            recibido: 'success',       // Verde
            facturado: 'processing',   // Azul
          };
          const estadoLabels = {
            recibido: 'RECIBIDO',
            facturado: 'FACTURADO',
          };

          // Ocultar estados según permisos
          if (estado === 'recibido' && !hasPermission('RECIBIDO_ETIQUETA_CS')) {
            return <Tag color="default">-</Tag>;
          }
          if (estado === 'facturado' && !hasPermission('FACTURADO_ETIQUETA_CS')) {
            return <Tag color="default">-</Tag>;
          }

          if (estado) {
            return <Tag color={estadoColors[estado]}>{estadoLabels[estado]}</Tag>;
          }
          return <Tag color="default">SIN ESTADO</Tag>;
        },
      },
    ];

    return cols;
  }, [hasPermission]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Dashboard - Control de Servicios</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Select
            value={rangoFecha}
            onChange={handleRangoChange}
            disabled={loading}
            style={{ width: 180 }}
          >
            <Option value="hoy">Hoy</Option>
            <Option value="ayer">Ayer</Option>
            <Option value="mañana">Mañana</Option>
            <Option value="semana">Esta Semana</Option>
            <Option value="mes">Este Mes</Option>
            <Option value="mes_anterior">Mes Anterior</Option>
            <Option value="personalizado">Personalizado</Option>
          </Select>

          {showCustomPicker && (
            <>
              <RangePicker
                value={customDateRange}
                onChange={handleCustomRangeChange}
                format="DD/MM/YYYY"
                disabledDate={(current) => {
                  return current && current > dayjs().endOf('day');
                }}
              />
              <Button
                type="primary"
                onClick={() => fetchEstadisticas('personalizado')}
                disabled={loading}
              >
                Aplicar
              </Button>
            </>
          )}

          {rangoFecha !== 'personalizado' && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Actualizar
            </Button>
          )}
        </div>
      </div>

      {/* Fila 1: Estadísticas principales */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Total de Controles"
              value={estadisticas.total}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Servicios Terminados"
              value={estadisticas.terminados}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Servicios Fallidos"
              value={estadisticas.fallidos}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#9B1C31', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Servicios Cancelados"
              value={estadisticas.cancelados}
              prefix={<StopOutlined />}
              valueStyle={{ color: '#ff4d4f', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Fila 2: Estadísticas por Estados de Auditoría (según permisos) */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        {hasPermission('RECIBIDO_ETIQUETA_CS') && (
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Recibido"
                value={estadisticas.porEstado?.recibido || 0}
                prefix={<TagsOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        )}
        {hasPermission('FACTURADO_ETIQUETA_CS') && (
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Facturado"
                value={estadisticas.porEstado?.facturado || 0}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        )}
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Sin Estado"
              value={estadisticas.porEstado?.sin_estado || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Fila 3: Gráfico de Estados de Auditoría */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col xs={24}>
          <Card title="Estados de Auditoría">
            <div style={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Bar
                data={estadosData}
                options={{
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: 1
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      display: false
                    }
                  }
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Fila 5: Controles recientes */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col span={24}>
          <Card title="Controles Recientes">
            <Table
              dataSource={estadisticas.recientes || []}
              columns={columnasRecientes}
              rowKey="id"
              pagination={{ pageSize: 5 }}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ControlServiciosDashboard;
